// Paste lines into a PO (M3): rows copied from a spreadsheet or the vendor's PI become PO lines in one go. The preview
// shows what each row matched (HHL's SKU, the vendor's SKU or a barcode), its qty, price and promised date, MOQ and
// multiple warnings, and the rows that can't go in; "Add N lines" adds the rest through po.add_lines
// (shared/po-paste.ts reads the paste). Each column's kind can be changed, and a paste without a header says its columns
// were worked out. How open POs from before HHL Ops and vendor PIs are re-entered quickly.
import { AlertTriangle, ClipboardPaste } from "lucide-react";
import { useMemo, useState } from "react";
import { planToday } from "../../../../shared/day.ts";
import type { Po, PoLine, Vendor } from "../../../../shared/db.gen.ts";
import { shortDate } from "../../../../shared/planning.ts";
import { priceFor } from "../../../../shared/po.ts";
import { COLUMN_KINDS, type ColumnKind, MAX_PASTE_LINES, readPaste } from "../../../../shared/po-paste.ts";
import { money, qty } from "../../lib/format.ts";
import { useRows } from "../../lib/sync.ts";
import { Button, cx, Textarea, Thumb } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { toast } from "../../ui/toast.tsx";
import { addLines, vendorLabel } from "./lib.ts";

const COLUMN_LABELS: Record<ColumnKind, string> = { id: "Item", qty: "Qty", price: "Unit price", amount: "Amount", date: "Date", text: "Ignore" };
const BY_LABELS = { sku: "SKU", vendor_sku: "their SKU", barcode: "barcode" } as const;

export function PasteLinesButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" variant="ghost" icon={<ClipboardPaste className="size-3.5" />} onClick={onClick} title="Paste rows from a spreadsheet or the vendor's PI">
      Paste lines
    </Button>
  );
}

export function PasteLinesDialog({ vendor, po, lines, onClose }: { vendor: Vendor; po: Po; lines: PoLine[]; onClose: () => void }) {
  const items = useRows("item");
  const allLinks = useRows("vendor_item");
  const [text, setText] = useState("");
  // What people set the columns to (cleared when the paste changes shape).
  const [chosen, setChosen] = useState<ColumnKind[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const today = planToday();
  const links = useMemo(() => allLinks.filter((l) => l.vendor_id === vendor.id), [allLinks, vendor.id]);
  const onPo = useMemo(() => new Map(lines.map((l) => [l.item_id, l.position])), [lines]);
  const result = useMemo(
    () => readPaste(text, { items, links, onPo, today, currency: po.currency, columns: chosen }),
    [text, items, links, onPo, today, po.currency, chosen],
  );
  const skipped = result.rows.length - result.ok.length;
  const tooMany = result.ok.length > MAX_PASTE_LINES;
  const setColumn = (k: number, kind: ColumnKind) => setChosen(result.columns.map((c, i) => (i === k ? kind : c)));
  // Spreadsheet letters, so "C: Qty" points at the column pasted.
  const columns = result.columns.map((kind, k) => ({ kind, letter: String.fromCharCode(65 + (k % 26)), header: result.header?.[k] ?? "" }));

  const submit = async () => {
    if (!result.ok.length || tooMany) return;
    setBusy(true);
    const ok = await addLines(
      po,
      lines,
      result.ok.map((r) => ({
        item: items.find((i) => i.id === r.item?.id) as (typeof items)[number],
        link: links.find((l) => l.item_id === r.item?.id),
        qty: r.qty ?? undefined,
        ...(r.price !== null ? { unit_price: r.price } : {}),
        ...(r.date ? { promised_date: r.date } : {}),
      })),
    );
    setBusy(false);
    if (!ok) return;
    toast.ok(
      `Added ${result.ok.length} line${result.ok.length === 1 ? "" : "s"} to ${po.number}${skipped ? `; ${skipped} row${skipped === 1 ? "" : "s"} left out` : ""}`,
    );
    onClose();
  };

  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex max-h-[86vh] w-[1000px] max-w-[calc(100vw-32px)] flex-col rounded-xl border border-line bg-surface shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="border-b border-line p-4">
          <div className="text-[15px] font-semibold">
            Paste lines into {po.number} · {vendorLabel(vendor)}
          </div>
          <div className="mt-0.5 text-[12px] text-muted">
            Copy the rows from a spreadsheet or the vendor's PI: an item (HHL's SKU, {vendorLabel(vendor)}'s SKU or the barcode), the quantity, and if you have
            them the unit price and promised date. A header row (SKU, Qty, Price, Date) helps; without one the columns are worked out.
          </div>
          <Textarea
            autoFocus
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setChosen(undefined);
            }}
            placeholder={"SKU\tQty\tUnit price\tPromised\nGBA-BTN-I\t1,200\t0.42\t12/15/2026"}
            className="mt-2 h-32 font-mono text-[12px]"
          />
        </div>
        {result.rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-line bg-surface-2 px-4 py-1.5 text-[12px] text-muted">
            <span>Columns:</span>
            {columns.map((c, k) => (
              <label
                key={c.letter}
                title={c.header ? `Column ${c.letter}, headed "${c.header}": what it holds` : `Column ${c.letter}: what it holds`}
                className={cx(
                  "inline-flex h-6 items-center gap-1 rounded px-1.5 text-[11px] font-medium",
                  c.kind === "text" ? "bg-surface text-muted" : "bg-accent-soft text-accent-text",
                )}
              >
                {c.letter}
                {c.header ? ` ${c.header}` : ""}:
                <select
                  value={c.kind}
                  onChange={(e) => setColumn(k, e.target.value as ColumnKind)}
                  className="cursor-pointer bg-transparent font-medium focus:outline-none"
                >
                  {COLUMN_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {COLUMN_LABELS[kind]}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <span className="ml-auto">
              {result.ok.length} to add{skipped ? ` · ${skipped} can't go in` : ""}
            </span>
          </div>
        )}
        {result.conflict && (
          <div className="flex items-center gap-2 border-b border-line bg-danger-soft px-4 py-1.5 text-[12px] text-danger">
            <AlertTriangle className="size-3.5 shrink-0" />
            {result.conflict}.
          </div>
        )}
        {result.rows.length > 0 && result.guessed && (
          <div className="flex items-center gap-2 border-b border-line bg-warn-soft px-4 py-1.5 text-[12px] text-warn">
            <AlertTriangle className="size-3.5 shrink-0" />
            No header row, so the columns were worked out from what they hold: check each one above (change any that's wrong), or paste the header row too.
          </div>
        )}
        {tooMany && (
          <div className="border-b border-line bg-danger-soft px-4 py-1.5 text-[12px] text-danger">
            {result.ok.length} lines: a PO takes {MAX_PASTE_LINES} at a time. Paste them in parts.
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {result.rows.map((r) => {
            const price = r.price ?? (r.link && r.qty ? priceFor(r.link, r.qty) : null);
            return (
              <div
                key={r.row}
                className={cx(
                  "grid grid-cols-[36px_28px_minmax(0,1.6fr)_80px_110px_90px_minmax(0,1.4fr)] items-start gap-x-2 border-b border-line/60 px-4 py-1.5",
                  r.problems.length && "bg-danger-soft/40",
                )}
              >
                <span className="pt-0.5 text-right text-[12px] text-faint">{r.row}</span>
                <Thumb src={r.item?.photo_url} size={24} />
                <span className="min-w-0">
                  {r.item ? (
                    <>
                      <span className="block truncate font-mono text-[12px]">{r.item.sku}</span>
                      <span className="block truncate text-[12px] text-muted">
                        {r.item.name}
                        {r.by && r.by !== "sku" && ` · by ${BY_LABELS[r.by]} ${r.key}`}
                      </span>
                    </>
                  ) : (
                    <span className="block truncate font-mono text-[12px] text-muted" title={r.cells.join(" | ")}>
                      {r.cells.join(" · ")}
                    </span>
                  )}
                </span>
                <span className={cx("pt-0.5 text-right font-mono text-[12px]", r.qtyWarnings.length && "text-warn")}>{r.qty === null ? "—" : qty(r.qty)}</span>
                <span className="pt-0.5 text-right font-mono text-[12px]">
                  {price === null ? (
                    <span className="text-warn">no price</span>
                  ) : (
                    <>
                      {money(price, po.currency)}
                      {r.price === null && <span className="block text-[10px] text-faint">{vendorLabel(vendor)}'s</span>}
                    </>
                  )}
                </span>
                <span className="pt-0.5 text-[12px] text-muted">{r.date ? shortDate(r.date, today) : ""}</span>
                <span className="text-[12px]">
                  {r.problems.map((p) => (
                    <span key={p} className="block text-danger">
                      {p}
                    </span>
                  ))}
                  {r.warnings.map((w) => (
                    <span key={w} className="block text-warn">
                      {w}
                    </span>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 border-t border-line p-3">
          <span className="text-[12px] text-muted">
            {skipped
              ? `Rows in red are left out: fix them in the paste, or add those items by search afterwards.`
              : result.ok.length
                ? "Quantities go in as pasted; MOQ and multiple warnings show on the lines with a one-click fix."
                : ""}
          </span>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !result.ok.length || tooMany} onClick={submit}>
            Add {result.ok.length || ""} {result.ok.length === 1 ? "line" : "lines"}
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}
